import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { systemReportsAPI } from '../lib/api';
import { 
  FolderOpen, LogOut, Shield, FileText, ChevronDown, Target, Calendar, 
  HardDrive, Activity, User, Scan, Brain, Building2, Settings, Check, Plus, 
  Bell, GraduationCap, Eye, Layers, Newspaper, BookOpen, ClipboardList,
  AlertTriangle, Wrench, UserCircle, LayoutDashboard, Ticket, FileBarChart,
  Briefcase, Calculator, FileSignature
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout, currentOrganization, organizations, switchOrganization } = useAuth();
  const [showResourcesMenu, setShowResourcesMenu] = useState(false);
  const [showToolsMenu, setShowToolsMenu] = useState(false);
  const [showMyCortexMenu, setShowMyCortexMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showOrgMenu, setShowOrgMenu] = useState(false);
  const [switchingOrg, setSwitchingOrg] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const resourcesMenuRef = useRef(null);
  const toolsMenuRef = useRef(null);
  const myCortexMenuRef = useRef(null);
  const userMenuRef = useRef(null);
  const orgMenuRef = useRef(null);

  // Fetch alert count
  useEffect(() => {
    const fetchAlertCount = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await systemReportsAPI.getAlertCount();
        setAlertCount(response.data.count || 0);
      } catch (error) {
        // Silently fail
      }
    };
    
    fetchAlertCount();
    const interval = setInterval(fetchAlertCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSwitchOrg = async (orgId) => {
    if (orgId === currentOrganization?.id) {
      setShowOrgMenu(false);
      return;
    }
    
    setSwitchingOrg(true);
    try {
      await switchOrganization(orgId);
      setShowOrgMenu(false);
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch organization:', error);
    } finally {
      setSwitchingOrg(false);
    }
  };

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resourcesMenuRef.current && !resourcesMenuRef.current.contains(event.target)) {
        setShowResourcesMenu(false);
      }
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target)) {
        setShowToolsMenu(false);
      }
      if (myCortexMenuRef.current && !myCortexMenuRef.current.contains(event.target)) {
        setShowMyCortexMenu(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (orgMenuRef.current && !orgMenuRef.current.contains(event.target)) {
        setShowOrgMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isResourcesActive = ['/resources', '/policies'].some(p => location.pathname.startsWith(p));
  const isToolsActive = ['/ssp', '/vulnerabilities', '/scorecard', '/calendar', '/incidents', '/risks', '/assets', '/pentest', '/grc'].some(p => location.pathname.startsWith(p));
  const isMyCortexActive = ['/dashboard', '/evidence', '/alerts'].some(p => location.pathname.startsWith(p));

  // Resources - Reference materials
  const resourcesMenuItems = [
    { path: '/virtual-ciso', label: 'Virtual CISO', icon: Shield, color: 'text-violet-400', highlight: true },
    { path: '/qbr', label: 'Quarterly Review (QBR)', icon: FileBarChart, color: 'text-emerald-400', highlight: true },
    { divider: true, label: 'MSP Tools' },
    { path: '/msp', label: 'Client Portfolio', icon: Briefcase, color: 'text-cyan-400' },
    { path: '/proposals', label: 'Proposal Generator', icon: Calculator, color: 'text-amber-400' },
    { path: '/contracts', label: 'Contract Management', icon: FileSignature, color: 'text-violet-400' },
    { divider: true, label: 'Intelligence & Resources' },
    { path: '/news', label: 'News & Threat Intel', icon: Newspaper, color: 'text-blue-400' },
    { path: '/resources', label: 'Resource Library', icon: FolderOpen, color: 'text-green-400' },
    { path: '/policies', label: 'Policy Library', icon: BookOpen, color: 'text-purple-400' },
  ];

  // Tools - Actionable items
  const toolsMenuItems = [
    { path: '/service-desk', label: 'IT Service Desk', icon: Ticket, color: 'text-cyan-400', highlight: true },
    { divider: true, label: 'Security Tools' },
    { path: '/scorecard', label: 'Security Scorecard', icon: Target, color: 'text-violet-400' },
    { path: '/calendar', label: 'Compliance Calendar', icon: Calendar, color: 'text-blue-400' },
    { path: '/ssp', label: 'System Security Plans', icon: FileText, color: 'text-purple-400' },
    { path: '/vulnerabilities', label: 'Vulnerability Scans', icon: AlertTriangle, color: 'text-red-400' },
    { path: '/pentest', label: 'Penetration Testing', icon: Scan, color: 'text-rose-400' },
    { path: '/incidents', label: 'Incident Response', icon: Activity, color: 'text-orange-400' },
    { path: '/risks', label: 'Risk Register', icon: Target, color: 'text-yellow-400' },
    { path: '/assets', label: 'Asset Inventory', icon: HardDrive, color: 'text-cyan-400' },
    { divider: true, label: 'GRC Suite' },
    { path: '/grc/training', label: 'Training Tracker', icon: GraduationCap, color: 'text-emerald-400' },
    { path: '/grc/vendors', label: 'Vendor Risk', icon: Building2, color: 'text-amber-400' },
    { path: '/grc/darkweb', label: 'Dark Web Monitoring', icon: Eye, color: 'text-purple-400' },
    { path: '/grc/controls', label: 'Control Mapping', icon: Layers, color: 'text-cyan-400' },
  ];

  // MyCortex - Personalized content (only for logged-in users)
  const myCortexMenuItems = [
    { path: '/dashboard', label: 'My Assessments', icon: ClipboardList, color: 'text-violet-400' },
    { path: '/my-ssp-reports', label: 'My SSP Reports', icon: FileText, color: 'text-emerald-400' },
    { path: '/evidence', label: 'Evidence Library', icon: FolderOpen, color: 'text-blue-400' },
    { path: '/dataroom', label: 'Data Room', icon: HardDrive, color: 'text-cyan-400' },
    { path: '/alerts', label: 'System Alerts', icon: Bell, color: 'text-amber-400', badge: alertCount },
  ];

  const DropdownMenu = ({ items, onNavigate }) => (
    <div className="absolute top-full left-0 mt-2 w-56 bg-slate-800 border border-slate-700 rounded-xl shadow-xl py-2 z-50">
      {items.map((item, index) => (
        item.divider ? (
          <div key={index} className="px-4 py-2 mt-1 border-t border-slate-700">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{item.label}</span>
          </div>
        ) : (
          <button
            key={item.path}
            onClick={() => onNavigate(item.path)}
            className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
              item.highlight 
                ? 'bg-violet-500/10 border-l-2 border-violet-500 hover:bg-violet-500/20'
                : isActive(item.path) || location.pathname.startsWith(item.path)
                  ? 'bg-violet-500/20 text-violet-300'
                  : 'text-slate-300 hover:bg-slate-700/50'
            }`}
            data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
          >
            <div className="flex items-center gap-3">
              <item.icon className={`w-4 h-4 ${item.color}`} />
              <span className={item.highlight ? 'text-violet-300 font-medium' : ''}>{item.label}</span>
            </div>
            {item.badge > 0 && (
              <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                {item.badge > 9 ? '9+' : item.badge}
              </span>
            )}
          </button>
        )
      ))}
    </div>
  );

  const NavDropdown = ({ label, icon: Icon, isOpen, setIsOpen, menuRef, items, isActiveCheck, testId }) => (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
          isActiveCheck
            ? 'bg-violet-600 text-white'
            : 'text-violet-200 hover:text-white hover:bg-violet-500/20'
        }`}
        data-testid={testId}
      >
        <Icon className="w-4 h-4" />
        {label}
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <DropdownMenu 
          items={items} 
          onNavigate={(path) => { navigate(path); setIsOpen(false); }} 
        />
      )}
    </div>
  );

  return (
    <nav className="bg-slate-900/95 backdrop-blur-lg border-b border-violet-500/20 sticky top-0 z-50">
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a 
            href="https://hipaa-compliance-hub-1.preview.emergentagent.com/"
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
            data-testid="nav-logo"
          >
            <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-white">Aspiritech</span>
              <span className="text-violet-400 text-xs block -mt-1">Cybersecurity</span>
            </div>
          </a>

          {/* Navigation Links */}
          <div className="flex items-center gap-2">
            {/* Resources Dropdown - Always visible */}
            <NavDropdown
              label="Resources"
              icon={BookOpen}
              isOpen={showResourcesMenu}
              setIsOpen={setShowResourcesMenu}
              menuRef={resourcesMenuRef}
              items={resourcesMenuItems}
              isActiveCheck={isResourcesActive}
              testId="nav-resources-dropdown"
            />

            {/* Tools Dropdown - Always visible */}
            <NavDropdown
              label="Tools"
              icon={Wrench}
              isOpen={showToolsMenu}
              setIsOpen={setShowToolsMenu}
              menuRef={toolsMenuRef}
              items={toolsMenuItems}
              isActiveCheck={isToolsActive}
              testId="nav-tools-dropdown"
            />

            {/* MyCortex Dropdown - Only when logged in */}
            {isAuthenticated && (
              <NavDropdown
                label="MyCortex"
                icon={LayoutDashboard}
                isOpen={showMyCortexMenu}
                setIsOpen={setShowMyCortexMenu}
                menuRef={myCortexMenuRef}
                items={myCortexMenuItems}
                isActiveCheck={isMyCortexActive}
                testId="nav-mycortex-dropdown"
              />
            )}

            {/* Theme Toggle */}
            <div className="ml-2">
              <ThemeToggle />
            </div>

            {isAuthenticated ? (
              <>
                {/* Organization Switcher */}
                {organizations && organizations.length > 0 && (
                  <div className="relative ml-2" ref={orgMenuRef}>
                    <button
                      onClick={() => setShowOrgMenu(!showOrgMenu)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium text-violet-200 hover:text-white hover:bg-violet-500/20 border border-violet-500/30"
                      data-testid="nav-org-switcher"
                    >
                      <Building2 className="w-4 h-4 text-violet-400" />
                      <span className="hidden md:block max-w-[120px] truncate">
                        {currentOrganization?.name || 'Select Org'}
                      </span>
                      <ChevronDown className={`w-3 h-3 transition-transform ${showOrgMenu ? 'rotate-180' : ''}`} />
                    </button>

                    {showOrgMenu && (
                      <div className="absolute top-full right-0 mt-2 w-64 bg-slate-800 border border-slate-700 rounded-xl shadow-xl py-2 z-50">
                        <div className="px-4 py-2 border-b border-slate-700">
                          <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Organizations</p>
                        </div>
                        
                        {organizations.map((org) => (
                          <button
                            key={org.id}
                            onClick={() => handleSwitchOrg(org.id)}
                            disabled={switchingOrg}
                            className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                              org.id === currentOrganization?.id
                                ? 'bg-violet-500/20 text-violet-300'
                                : 'text-slate-300 hover:bg-slate-700/50'
                            } ${switchingOrg ? 'opacity-50' : ''}`}
                            data-testid={`nav-org-${org.id}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                org.id === currentOrganization?.id ? 'bg-violet-600' : 'bg-slate-700'
                              }`}>
                                <Building2 className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <p className="font-medium">{org.name}</p>
                                <p className="text-xs text-slate-500 capitalize">{org.role}</p>
                              </div>
                            </div>
                            {org.id === currentOrganization?.id && (
                              <Check className="w-4 h-4 text-violet-400" />
                            )}
                          </button>
                        ))}

                        <div className="border-t border-slate-700 mt-2 pt-2">
                          <button
                            onClick={() => { navigate('/organizations/settings'); setShowOrgMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700/50 transition-colors"
                            data-testid="nav-org-settings"
                          >
                            <Settings className="w-4 h-4 text-slate-400" />
                            Organization Settings
                          </button>
                          <button
                            onClick={() => { navigate('/organizations/new'); setShowOrgMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-slate-700/50 transition-colors"
                            data-testid="nav-org-create"
                          >
                            <Plus className="w-4 h-4 text-slate-400" />
                            Create Organization
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* User Dropdown */}
                <div className="relative ml-2" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium text-violet-200 hover:text-white hover:bg-violet-500/20"
                    data-testid="nav-user-menu"
                  >
                    <div className="w-7 h-7 bg-violet-600 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="hidden sm:block">
                      {user?.firstName || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className={`w-3 h-3 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                  </button>

                  {showUserMenu && (
                    <div className="absolute top-full right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-xl shadow-xl py-2 z-50">
                      <div className="px-4 py-2 border-b border-slate-700">
                        <p className="text-sm font-medium text-white">
                          {user?.firstName} {user?.lastName}
                        </p>
                        <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                      </div>

                      {user?.isAdmin && (
                        <button
                          onClick={() => { navigate('/admin'); setShowUserMenu(false); }}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                            isActive('/admin')
                              ? 'bg-violet-500/20 text-violet-300'
                              : 'text-slate-300 hover:bg-slate-700/50'
                          }`}
                          data-testid="nav-admin-link"
                        >
                          <Shield className="w-4 h-4 text-amber-400" />
                          Admin Dashboard
                        </button>
                      )}

                      <button
                        onClick={() => { handleLogout(); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                        data-testid="nav-logout"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Single CTA for logged-out users */
              <button
                onClick={() => navigate('/login')}
                className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg transition-all text-sm font-medium shadow-lg shadow-violet-500/25"
                data-testid="nav-get-started"
              >
                <UserCircle className="w-4 h-4" />
                Get Started
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
